import { createStyles } from 'antd-style';

import {
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../../theme';
import { FA_SELECTOR } from '../../styles/variables';
import { borderRadius } from '../../styles/mixins';

export const useInvoiceStyles = createStyles(({ token }) => ({
  container: {
    width: '100%',
    paddingTop: 14,
    paddingLeft: ThemeSpaces.SPACE_NORM_30,
    paddingRight: ThemeSpaces.SPACE_NORM_30,
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    lineHeight: '26px',
    color: token.colorPrimary,
    fontSize: token.fontSizeLG,
    fontWeight: 'bold',
    marginTop: 19,
  },
  drawer: {
    '&.NewShipmentDrawer.ant-drawer-open .ant-drawer-content-wrapper': {
      [`@media (min-width: ${token.screenLGMin}px)`]: {
        width: '800px !important',
      },

      '.ant-drawer-body': {
        paddingRight: 0,
        paddingTop: 0,
      },
    },
  },
  section: {
    backgroundColor: ThemeColors.COLOR_WHITE3,
    borderRadius: token.borderRadius,
    boxShadow: `0 0 0 1px ${ThemeColors.COLOR_GREY7_8}, 3px 3px 15px 0 ${hexToRgbaWithAlpha(
      ThemeColors.COLOR_GREY_BLUISH3,
      0.5
    )}`,
  },
  textContainer: {
    marginTop: 9,
    marginBottom: ThemeSpaces.SPACE_NORM,
  },
  loading: {
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 14,
  },
  toolbar: {
    padding: `0 ${ThemeSpaces.SPACE_NORM}px`,
    backgroundColor: ThemeColors.COLOR_WHITE2_5,
    borderRadius: token.borderRadius,
    boxShadow: `0 0 0 2px ${token.colorWhite}, 1px 1px 20px 0 ${ThemeColors.COLOR_GREY_BLUISH4_1}`,
    lineHeight: '70px',
    display: 'flex',
    width: '100% !important',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectedAccount: {
    lineHeight: '24px',
    color: ThemeColors.COLOR_GREY3,
    fontSize: 24,
    fontWeight: 300,
  },
  content: {
    [`@media (min-width: ${token.screenLGMin}px)`]: {
      minHeight: 405,
      maxHeight: 520,
    },
    [`@media (min-width: ${token.screenMDMin}px) and (max-width: ${token.screenMDMax}px)`]:
      {
        minHeight: 300,
        maxHeight: 360,
      },
    [`@media (max-width: ${token.screenSMMax}px)`]: {
      minHeight: 240,
      maxHeight: 300,
    },

    display: 'flex',
    flexDirection: 'column',
    gap: ThemeSpaces.SPACE_NORM,
    margin: `${ThemeSpaces.SPACE_NORM_15}px 0`,
  },
  selectionSectionTitle: {
    height: 40,
    lineHeight: 1,
    padding: `0 ${ThemeSpaces.SPACE_NORM_30}px`,
    display: 'flex',
    alignItems: 'center',
    fontFamily: ThemeFont.FONT_FAMILY6,
    fontSize: ThemeFont.FONT_SIZE_30,
    textTransform: 'uppercase',
    color: token.colorWhite,
    backgroundColor: ThemeColors.COLOR_GREY_BLUISH7_2,
    ...borderRadius({ size: token.borderRadiusSM, side: 'top' }),

    '.ant-col': {
      fontFamily: ThemeFont.FONT_FAMILY6,
      fontSize: ThemeFont.FONT_SIZE_30,
    },

    [FA_SELECTOR]: {
      color: ThemeColors.COLOR_GREY_BLUISH,
    },
  },
  selectionSectionBody: {
    padding: `${ThemeSpaces.SPACE_XS}px ${ThemeSpaces.SPACE_NORM_30}px 0 ${ThemeSpaces.SPACE_NORM_30}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: ThemeSpaces.SPACE_NORM,
  },
  getInvoicesContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: ThemeSpaces.SPACE_NORM,

    button: {
      borderRadius: 4,
      width: 100,
    },
  },
  disabledSearchWrapper: {
    opacity: 0.35,
    pointerEvents: 'none',
    cursor: 'not-allowed',
  },
  summaryBox: {
    borderTop: `2px solid ${token.colorWhite}`,
    borderBottom: `2px solid ${ThemeColors.COLOR_GREY8_1}`,
    padding: '8px 0',
    marginBottom: 26,
    marginTop: ThemeSpaces.SPACE_NORM_15,
  },
  summaryContainerBox: {
    borderTop: `2px solid ${ThemeColors.COLOR_GREY8_1}`,
    borderBottom: `2px solid ${token.colorWhite}`,
    padding: '4px 0',
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
  },
  summaryItem: {
    textAlign: 'center',

    '.title': {
      fontWeight: 700,
      fontSize: 'small',
      color: ThemeColors.COLOR_GREY_BLUISH,
      textTransform: 'uppercase',
    },

    '.value': {
      marginTop: token.marginXXS,
      color: ThemeColors.COLOR_GREY3,
      fontSize: 'medium',
      fontWeight: 600,
    },
  },
  transactionHistorySummaryBox: {
    marginBottom: 6,
  },
  printButton: {
    '&.IconButton': {
      color: token.colorPrimary,

      '.IconButton-Label': {
        fontFamily: token.fontFamily,
        fontWeight: 500,
      },

      [FA_SELECTOR]: {
        fontSize: 18,
        marginBottom: ThemeSpaces.SPACE_XXS,
      },
    },
  },
  text: {
    fontSize: ThemeFont.FONT_SIZE_XS,
    fontWeight: 500,
    textAlign: 'center',
    color: token.colorPrimary,
    textTransform: 'uppercase',
  },
  number: {
    display: 'flex',
    flexDirection: 'column',
  },
  numberText: {
    width: '100%',
    lineHeight: '16px',
    color: ThemeColors.COLOR_GREY_BLUISH,
    fontSize: ThemeFont.FONT_SIZE_XXS,
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  numberValue: {
    width: '100%',
    lineHeight: '24px',
    color: ThemeColors.COLOR_GREY3,
    fontSize: token.fontSizeLG,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  count: {
    fontWeight: 400,
    fontSize: ThemeFont.FONT_SIZE_20,
    fontStyle: 'normal',
    lineHeight: '34px',
    color: ThemeColors.COLOR_GREY6,
  },
  textWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer',
    alignItems: 'center',
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,

    '.ant-table-cell .ant-typography': {
      marginBottom: 0,
    },
  },
  invoiceReference: {
    '.ant-typography': {
      fontSize: token.fontSizeSM,

      strong: {
        marginRight: 4,
      },
    },
  },
  transactionHistoryLegend: {
    height: 14,
    fontSize: token.fontSizeSM,
    fontWeight: 500,
    marginBottom: 6,
  },
  transactionHistoryTableRow: {
    fontWeight: 500,
  },
  jobChargesPopover: {
    '--antd-arrow-background-color': ThemeColors.COLOR_WHITE9,

    '.ant-popover-arrow': {
      left: 45,

      '&::after': {
        border: `1px solid ${ThemeColors.COLOR_GREY7_6}`,
      },
    },
  },
  jobChargesPopoverBody: {
    '.ant-popover-title': {
      border: `1px solid ${ThemeColors.COLOR_GREY9_5}`,
      borderRadius: `${token.borderRadiusSM}px ${token.borderRadiusSM}px 0 0`,
      fontWeight: 400,
      padding: `6px ${ThemeSpaces.SPACE_SM}px 3px ${ThemeSpaces.SPACE_SM}px`,
      marginBottom: 0,

      '.ant-row': {
        lineHeight: '14px',

        '.ant-col:first-child': {
          fontSize: token.fontSizeSM,
        },
      },

      '.CopyToClipboard-Icon': {
        color: ThemeColors.COLOR_GREY_BLUISH7_3,
      },
    },

    '.ant-popover-inner-content': {
      backgroundColor: ThemeColors.COLOR_WHITE9,
      border: `1px solid ${ThemeColors.COLOR_GREY7_6}`,
      borderTop: 'none',
      borderRadius: `0 0 ${token.borderRadiusSM}px ${token.borderRadiusSM}px`,
      padding: `${ThemeSpaces.SPACE_NORM0_5}px ${ThemeSpaces.SPACE_NORM}px`,
    },
  },
  jobChargesRow: {
    '&&&': {
      padding: 0,
      lineHeight: 1.2,

      '&:last-child': {
        fontWeight: 600,
      },
      '.ant-col': {
        fontSize: token.fontSizeSM,
      },
    },
  },
}));
